import {RespinSymbolData} from '@fantasmagames/slot/src/logic/Logic';
import {WinLine} from '@fantasmagames/slot/src/logic/WinLine';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';

export enum ExplosionDirection {
	BIG_ONE = 0,
	LEFT_TOP = 1,
	RIGHT_TOP = 2,
	RIGHT_BOTTOM = 3,
	LEFT_BOTTOM = 4,
}

export class ExpandingWildReel {
	private readonly _reelIndex: number;
	private readonly _rowIndex: number;
	private readonly _multiplier: number;

	constructor(reelIndex: number, rowIndex: number, multiplier: number) {
		this._reelIndex = reelIndex;
		this._rowIndex = rowIndex;
		this._multiplier = multiplier;
	}

	public get reelIndex(): number {
		return this._reelIndex;
	}

	public get rowIndex(): number {
		return this._rowIndex;
	}

	public get multiplier(): number {
		return this._multiplier;
	}
}

export class RespinScatterInfo {
	private readonly _respinScatterPosition: SymbolPosition;
	private readonly _respinSymbols: SymbolPosition[];

	constructor(respinScatterPosition: SymbolPosition, respinSymbols: SymbolPosition[]) {
		this._respinScatterPosition = respinScatterPosition;
		this._respinSymbols = respinSymbols;
	}

	public get respinScatterPosition(): SymbolPosition {
		return this._respinScatterPosition;
	}

	public get respinSymbols(): SymbolPosition[] {
		return this._respinSymbols;
	}
}

export class ExplosionInfo {
	private readonly _explosionSourcePosition: SymbolPosition;
	private readonly _explodedSymbols: SymbolPosition[];
	private readonly _explosionDirection: ExplosionDirection;

	constructor(explosionSourcePosition: SymbolPosition, explodedSymbols: SymbolPosition[], explosionDirection: ExplosionDirection) {
		this._explosionSourcePosition = explosionSourcePosition;
		this._explodedSymbols = explodedSymbols;
		this._explosionDirection = explosionDirection;
	}

	public get explosionSourcePosition(): SymbolPosition {
		return this._explosionSourcePosition;
	}

	public get explodedSymbols(): SymbolPosition[] {
		return this._explodedSymbols;
	}

	public get explosionDirection(): ExplosionDirection {
		return this._explosionDirection;
	}
}

export class RespinNewSymbol {
	private _reel: number;
	private _symbol: number;
	private _height: number;

	constructor(reel: number, symbol: number, height: number) {
		this._reel = reel;
		this._symbol = symbol;
		this._height = height;
	}

	public get reel(): number {
		return this._reel;
	}

	public get symbol(): number {
		return this._symbol;
	}

	public get height(): number {
		return this._height;
	}
}

export class GameWinLine extends WinLine {
	protected _betways: number;
	protected _reelsInWin: number;

	constructor(
		index: number,
		symbol: number,
		positions: SymbolPosition[],
		winPositions: SymbolPosition[],
		win: number,
		betways: number,
		reelsInWin: number,
	) {
		super(index, symbol, positions, winPositions, win);
		this._betways = betways;
		this._reelsInWin = reelsInWin;
	}

	public get betways(): number {
		return this._betways;
	}

	public get reelsInWin(): number {
		return this._reelsInWin;
	}
}

export class LevelData {
	private readonly _position: number;
	private readonly _offset: number;
	private readonly _currentLevel: number;

	constructor(position: number, offset: number, currentLevel: number) {
		this._position = position;
		this._offset = offset;
		this._currentLevel = currentLevel;
	}

	public get position(): number {
		return this._position;
	}

	public get offset(): number {
		return this._offset;
	}

	public get currentLevel(): number {
		return this._currentLevel;
	}
}

export class AvalancheNewSymbol {
	private _reel: number;
	private _row: number;
	private _symbol: number;
	private _height: number;

	constructor(reel: number, row: number, symbol: number, height: number) {
		this._reel = reel;
		this._row = row;
		this._symbol = symbol;
		this._height = height;
	}

	public get reel(): number {
		return this._reel;
	}

	public get row(): number {
		return this._row;
	}

	public get symbol(): number {
		return this._symbol;
	}

	public get height(): number {
		return this._height;
	}
}

export class SpinResult {
	protected _balance: number;
	protected _stopPositions: number[];
	protected _spinWin: number;
	protected _nextSpinWin: number;
	protected _win: number;
	protected _winLines: WinLine[];
	protected _reels: GameSymbol[][];
	protected _previousReels: GameSymbol[][];

	protected _symbolsToRemove: SymbolPosition[];
	protected _symbolsToAdd: AvalancheNewSymbol[];
	protected _respinNewSymbols: AvalancheNewSymbol[];

	protected _individualSymbolsRespins: RespinSymbolData[];
	protected _individualSymbolsLocked: RespinSymbolData[];
	protected _individualSymbolsNewSymbols: GameSymbol[];

	protected _expandingWilds: ExpandingWildReel[] = [];
	protected _oldWilds: ExpandingWildReel[] = [];

	protected _respinScatters: RespinScatterInfo[];
	protected _explosions: ExplosionInfo[];

	protected _firstChestPrize: number;

	protected _totalBoxesInBonusGame: number;

	protected _expandedWildsToRemove: number[];

	protected _multiplier: number;

	protected _waysToWin: number;

	public get stopPositions(): number[] {
		return this._stopPositions;
	}

	public get spinWin(): number {
		return this._spinWin;
	}

	public get nextSpinWin(): number {
		return this._nextSpinWin;
	}

	public get win(): number {
		return this._win;
	}

	public get winLines(): WinLine[] {
		return this._winLines;
	}

	public get reels(): GameSymbol[][] {
		return this._reels;
	}

	public get previousReels(): GameSymbol[][] {
		return this._previousReels;
	}

	public get balance(): number {
		return this._balance;
	}

	public get symbolsToRemove(): SymbolPosition[] {
		return this._symbolsToRemove;
	}

	public get symbolsToAdd(): AvalancheNewSymbol[] {
		return this._symbolsToAdd;
	}

	public get respinNewSymbols(): AvalancheNewSymbol[] {
		return this._respinNewSymbols;
	}

	public get expandingWilds(): ExpandingWildReel[] {
		return this._expandingWilds;
	}

	public get individualSymbolsRespins(): RespinSymbolData[] {
		return this._individualSymbolsRespins;
	}

	public get expandedWildsToRemove(): number[] {
		return this._expandedWildsToRemove;
	}

	public get respinScatters(): RespinScatterInfo[] {
		return this._respinScatters;
	}

	public get explosions(): ExplosionInfo[] {
		return this._explosions;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/logic/SpinResult.ts